import { defineComponent as _defineComponent } from 'vue'
import {
        getCarStatusText,
        getChargingPlugTypeText,
        getTHGPayoff,
        getVehicleClassEnumMembers,
        getVehicleClassText,
    } from '@/utils/thg';
    import { THGCar } from '@/interfaces/thg/THGCar';
    import { computed, ref, watch } from 'vue';
    import { THGCarStatus } from '@/enums/thg/THGCarStatus';
    import {
        THGCarCombiPayoffAmount,
        THGCarFixPayoffAmount,
        THGCarFlexPayoffAmount,
        THGCarPayoffModel,
    } from '@/enums/thg/THGCarPayoffModel';
    import { Country } from '@/enums/Country';
    import { useTHGGlobalStore } from '@/storenew/thg/global';
    import { FormMode } from '@/enums/misc';
    import { add as addToDate, parseISO as parseISODate } from 'date-fns';
    import { focusElemRef } from '@/utils/misc';
    import { THGChargingPlugType } from '@/enums/thg/THGChargingPlugType';
    import { AustrianVRCType } from '@/enums/thg/THGAustrianVRCType';
    import THGTagInput from '@/components/thg/THGTagInput.vue';

    interface Props {
        value: THGCar;
        austrianVRCType?: AustrianVRCType;
        strictVinPattern: boolean;
        mode: FormMode;
    }

    interface YearSelection {
        value: number | number[];
        label: string;
    }

    
export default /*#__PURE__*/_defineComponent({
  __name: 'THGCarForm',
  props: {
    value: null,
    austrianVRCType: null,
    strictVinPattern: { type: Boolean },
    mode: null
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ({
        (e: 'input', car: THGCar): void;
    }), expose: any, slots: any, attrs: any }) {

const props = __props as {
        value: THGCar;
        austrianVRCType?: AustrianVRCType;
        strictVinPattern: boolean;
        mode: FormMode;
    };

    

    

    const { currentCountry } = useTHGGlobalStore();

    const vinInputElem = ref(null);

    // provide the possibility to force disabling the strict vin pattern though property is set to true
    const overrideDisableStrictVinPatternProp = ref<boolean>(false);

    watch<boolean>(overrideDisableStrictVinPatternProp, () => {
        if (vinInputElem.value) {
            // @ts-ignore
            const vinRawInputElem = vinInputElem.value.$el.querySelector(
                'input',
            ) as HTMLInputElement;
            focusElemRef(vinRawInputElem);
            vinRawInputElem.blur();
            vinRawInputElem.checkValidity();
        }
    });

    const car = computed<THGCar>({
        get() {
            return props.value;
        },
        set(newVal: THGCar) {
            emit('input', newVal);
        },
    });

    if (props.mode === FormMode.EDIT) {
        // Handle a payoff model change

        watch(
            () => car.value.payoff_model,
            (newVal: THGCarPayoffModel | undefined) => {
                const { payoffs } = useTHGGlobalStore();

                if (newVal && payoffs) {
                    let payoff;

                    switch (newVal) {
                        case THGCarPayoffModel.COMBI_FIX:
                            if (!car.value.combi_fix_payoff_amount) {
                                payoff = getTHGPayoff(
                                    payoffs,
                                    THGCarPayoffModel.COMBI_FIX,
                                    car.value.year,
                                ) as THGCarCombiPayoffAmount;

                                if (payoff) {
                                    car.value.combi_fix_payoff_amount = payoff;
                                } else {
                                    car.value.combi_fix_payoff_amount = {
                                        default: 0,
                                        years: [2025, 2026], // @TODO: add new logic if a new combination exists
                                    };
                                }
                            }
                            break;
                        case THGCarPayoffModel.COMBI_EXPRESS:
                            if (!car.value.combi_express_payoff_amount) {
                                payoff = getTHGPayoff(
                                    payoffs,
                                    THGCarPayoffModel.COMBI_EXPRESS,
                                    car.value.year,
                                ) as THGCarCombiPayoffAmount;

                                if (payoff) {
                                    car.value.combi_express_payoff_amount = payoff;
                                } else {
                                    car.value.combi_express_payoff_amount = {
                                        default: 0,
                                        years: [2025, 2026], // @TODO: add new logic if a new combination exists
                                    };
                                }
                            }
                            break;
                        case THGCarPayoffModel.FIX:
                            if (!car.value.fix_payoff_amount) {
                                payoff = getTHGPayoff(
                                    payoffs,
                                    THGCarPayoffModel.FIX,
                                    car.value.year,
                                ) as THGCarFixPayoffAmount;

                                if (payoff) {
                                    car.value.fix_payoff_amount = payoff;
                                } else {
                                    car.value.fix_payoff_amount = {
                                        default: 0,
                                        first: undefined,
                                    };
                                }
                            }
                            break;
                        case THGCarPayoffModel.EXPRESS:
                            if (!car.value.express_payoff_amount) {
                                payoff = getTHGPayoff(
                                    payoffs,
                                    THGCarPayoffModel.EXPRESS,
                                    car.value.year,
                                ) as THGCarFixPayoffAmount;

                                if (payoff) {
                                    car.value.express_payoff_amount = payoff;
                                } else {
                                    car.value.express_payoff_amount = {
                                        default: 0,
                                        first: undefined,
                                    };
                                }
                            }
                            break;
                        case THGCarPayoffModel.FLEX:
                            if (!car.value.flex_payoff_amount) {
                                payoff = getTHGPayoff(
                                    payoffs,
                                    THGCarPayoffModel.FLEX,
                                    car.value.year,
                                ) as THGCarFlexPayoffAmount;

                                if (payoff) {
                                    car.value.flex_payoff_amount = payoff;
                                } else {
                                    car.value.flex_payoff_amount = {
                                        min: 0,
                                        max: 0,
                                    } as THGCarFlexPayoffAmount;
                                }
                            }
                            break;
                    }
                }
            },
        );
    }

    const selectedYearOptionValue = ref<number[] | number>(car.value.year);

    watch(
        selectedYearOptionValue,
        (value: number | number[] | undefined) => {
            if (value) {
                if (Array.isArray(value)) {
                    car.value.year = value[0];
                    car.value.combi_years = value;
                } else {
                    car.value.year = value;
                    car.value.combi_years = undefined;
                }
            }
        },
        { immediate: true },
    );

    const yearSelections = computed<YearSelection[]>(() => {
        const years = [];

        if (props.mode === FormMode.EDIT) {
            years.push(
                { value: 2022, label: '2022' },
                { value: 2023, label: '2023' },
                { value: 2024, label: '2024' },
                { value: 2025, label: '2025' },
            );
        }

        if (currentCountry === Country.AT && props.mode === FormMode.CREATE) {
            years.push({ value: 2025, label: '2025' });
        }

        years.push({ value: 2026, label: '2026' }, { value: 2027, label: '2027' });
        years.push({ value: [2025, 2026], label: '2025 & 2026' });
        years.push({ value: [2026, 2027], label: '2026 & 2027' });

        return years;
    });

    // https://www.freecodecamp.org/news/regex-for-date-formats-what-is-the-regular-expression-for-matching-dates/
    const dateFormatRegex = /^([0-9]{2})?[0-9]{2}(-)(1[0-2]|0?[1-9])\2(3[01]|[12][0-9]|0?[1-9])$/;

    // minimum deregistration date (AT): registration date + one day
    const minDeregistrationDate = addToDate(parseISODate(car.value.registration_date as string), {
        days: 1,
    });

    // maximum deregistration date (AT)
    const maxDeregistrationDate = new Date(`${car.value.year}-12-31`);

    const vinPattern = computed<RegExp>(() => {
        if (props.strictVinPattern && !overrideDisableStrictVinPatternProp.value) {
            return /^[A-HJ-NPR-Z0-9]{17}$/;
        }

        return /^[A-Z0-9]{6,17}$/;
    });

    function ubaSubmittedDateRequired(): boolean {
        return (
            car.value.status === THGCarStatus.UBA_SUBMITTED ||
            car.value.status === THGCarStatus.CERT_RECEIVED ||
            car.value.status === THGCarStatus.CERT_DECLINED ||
            car.value.status === THGCarStatus.INCOME_PAID_OUT
        );
    }

return { __sfc: true,props, emit, currentCountry, vinInputElem, overrideDisableStrictVinPatternProp, car, selectedYearOptionValue, yearSelections, dateFormatRegex, minDeregistrationDate, maxDeregistrationDate, vinPattern, ubaSubmittedDateRequired, getCarStatusText, getChargingPlugTypeText, getVehicleClassEnumMembers, getVehicleClassText, THGCarStatus, THGCarPayoffModel, Country, FormMode, THGChargingPlugType, AustrianVRCType, THGTagInput }
}

})